const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

// ── Auth Token Management ──────────────────────────────────────────────
const TOKEN_KEY = 'elite_healthcare_token';
const REFRESH_TOKEN_KEY = 'elite_healthcare_refresh_token';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);
export const setRefreshToken = (token: string): void => localStorage.setItem(REFRESH_TOKEN_KEY, token);
export const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);
export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// ── Token Refresh ────────────────────────────────────────────────────
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  // Don't attempt refresh for mock sessions
  if (rt.startsWith('mock_session_')) return true;
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!res.ok) { clearToken(); return false; }
    const data = await res.json();
    if (data.accessToken) setToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    return true;
  } catch {
    clearToken();
    return false;
  }
}

// ── Generic Fetch Wrapper ──────────────────────────────────────────────
async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  _isRetry = false,
): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Auto-refresh on 401
  if (res.status === 401 && !_isRetry) {
    if (!refreshPromise) refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
    const refreshed = await refreshPromise;
    if (refreshed) return request<T>(endpoint, options, true);
    throw new Error('Session expired. Please log in again.');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `API error ${res.status}`);
  }

  // Handle 204 No Content
  if (res.status === 204) return {} as T;

  const json = await res.json();
  // Backend wraps responses in { success, data } via TransformResponseInterceptor
  return json.data !== undefined ? json.data : json;
}

// ── Query-string helper ────────────────────────────────────────────────
function qs(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

// ── Auth ───────────────────────────────────────────────────────────────
export const auth = {
  requestOtp: (cfmsId: string) =>
    request<{ message: string }>('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ cfmsId }),
    }),

  verifyOtp: async (cfmsId: string, otp: string) => {
    const res = await request<{ accessToken: string; refreshToken?: string; user: any }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ cfmsId, otp }),
    });
    if (res.accessToken) setToken(res.accessToken);
    if (res.refreshToken) setRefreshToken(res.refreshToken);
    return res;
  },
};

// ── Dashboard ──────────────────────────────────────────────────────────
export const dashboard = {
  getStats: () => request<any>('/dashboard/stats'),
  getReconciliationReport: () => request<any>('/dashboard/reconciliation-report'),
  getDisbursementReport: () => request<any>('/dashboard/disbursement-report'),
  getCategoryReport: () => request<any>('/dashboard/category-report'),
  getHospitalReport: () => request<any>('/dashboard/hospital-report'),
  getSlaReport: () => request<any>('/dashboard/sla-report'),
};

// ── Users / Officers ───────────────────────────────────────────────────
export const users = {
  getAll: (query?: Record<string, string | number | boolean | undefined>) =>
    request<any>(`/users${qs(query)}`),

  getById: (id: string) => request<any>(`/users/${id}`),

  create: (data: any) =>
    request<any>('/users', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: any) =>
    request<any>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

// ── LOC Applications ───────────────────────────────────────────────────
export const loc = {
  getAll: (query?: Record<string, string | number | boolean | undefined>) =>
    request<any>(`/loc${qs(query)}`),

  getById: (id: string) => request<any>(`/loc/${id}`),

};

// ── Hospitals ──────────────────────────────────────────────────────────
export const hospitals = {
  getAll: (query?: Record<string, string | number | boolean | undefined>) =>
    request<any>(`/hospitals${qs(query)}`),

  getById: (id: string) => request<any>(`/hospitals/${id}`),

  verify: (id: string) =>
    request<any>(`/hospitals/${id}/verify`, { method: 'POST' }),

  toggleBlacklist: (id: string) =>
    request<any>(`/hospitals/${id}/blacklist`, { method: 'POST' }),
};

// ── Audit Logs ─────────────────────────────────────────────────────────
export const auditLogs = {
  getAll: (query?: Record<string, string | number | boolean | undefined>) =>
    request<any>(`/audit-logs${qs(query)}`),
};

// ── Approvals ──────────────────────────────────────────────────────────
export const approvals = {
  getPending: (query?: Record<string, string | number | boolean | undefined>) =>
    request<any>(`/approvals/pending${qs(query)}`),

  getChains: () => request<any>('/approvals/chains'),

  approve: (locId: string, remarks?: string) =>
    request<any>('/approvals/' + locId + '/approve', {
      method: 'POST',
      body: JSON.stringify({ remarks }),
    }),

  reject: (locId: string, reason: string) =>
    request<any>('/approvals/' + locId + '/reject', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  getSettlementSummary: (locId: string) =>
    request<any>('/approvals/' + locId + '/settlement-summary'),

  approveSettlement: (locId: string, remarks?: string) =>
    request<any>('/approvals/' + locId + '/approve-settlement', {
      method: 'POST',
      body: JSON.stringify({ remarks }),
    }),
};

// ── Emergency ──────────────────────────────────────────────────────────
export const emergency = {
  getAll: (query?: Record<string, string | number | boolean | undefined>) =>
    request<any>(`/loc${qs({ ...query, isEmergency: true })}`),
};

// ── Family ─────────────────────────────────────────────────────────────
export const family = {
  getAll: () => request<any>('/family'),
};

// ── Documents ─────────────────────────────────────────────────────────
export const documents = {
  getByEntity: (entityType: string, entityId: string) =>
    request<any[]>(`/documents/entity/${entityType}/${entityId}`),

  getById: (id: string) => request<any>(`/documents/${id}`),

  getDownloadUrl: (id: string) => `${BASE_URL}/documents/${id}/download?token=${getToken() || ''}`,
};

// ── Default export (namespace-style) ───────────────────────────────────
const api = {
  auth,
  dashboard,
  users,
  loc,
  hospitals,
  auditLogs,
  approvals,
  emergency,
  family,
  documents,
};

export default api;
